import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import Plotly from 'plotly.js-dist-min';

export type CyclePopSpecies = 'cattle' | 'sheep' | 'goat' | 'buffalo' | 'pig';

const SPECIES_OPTIONS: { id: CyclePopSpecies; label: string; key: string }[] = [
  { id: 'cattle', label: 'Cattle', key: 'cattle_pop' },
  { id: 'sheep', label: 'Sheep', key: 'sheep_pop' },
  { id: 'goat', label: 'Goats', key: 'goat_pop' },
  { id: 'buffalo', label: 'Buffaloes', key: 'buffalo_pop' },
  { id: 'pig', label: 'Pigs', key: 'pig_pop' },
];

const RISK_COLORS = {
  high: '#f5b4b4',
  low: '#b7e0c2',
  mixed: '#f0d9a0',
  unknown: '#d4d4d8',
};

const RISK_LEGEND = [
  { cls: 'high' as const, label: 'High', color: RISK_COLORS.high },
  { cls: 'low' as const, label: 'Low', color: RISK_COLORS.low },
  { cls: 'mixed' as const, label: 'Mixed', color: RISK_COLORS.mixed },
];

function riskClass(risk: string | null | undefined): 'high' | 'low' | 'mixed' | 'unknown' {
  const r = (risk || '').trim().toLowerCase();
  if (!r || r === '—' || r === '-') return 'unknown';
  if (r === 'high') return 'high';
  if (r === 'low') return 'low';
  if (r.includes('high') && r.includes('low')) return 'mixed';
  if (r.includes('%')) return 'mixed';
  return 'unknown';
}

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface CycleAnimalPopulationVizProps {
  population: any[];
  year: number;
  inventoryYear?: number | null;
  inventoryIsFallback?: boolean;
}

const CycleAnimalPopulationViz: React.FC<CycleAnimalPopulationVizProps> = ({
  population,
  year,
  inventoryYear = null,
  inventoryIsFallback = false,
}) => {
  const [species, setSpecies] = useState<CyclePopSpecies>('cattle');
  const riskRef = useRef<HTMLDivElement | null>(null);
  const treeRef = useRef<HTMLDivElement | null>(null);

  const speciesMeta = SPECIES_OPTIONS.find((s) => s.id === species) || SPECIES_OPTIONS[0];

  const rows = useMemo(
    () =>
      (population || []).map((row) => ({
        name: String(row.district_province || row.district || '—'),
        risk: String(row.risk || ''),
        riskCls: riskClass(row.risk),
        value: numOrNull(row[speciesMeta.key]),
        epiunits: numOrNull(row.distinct_epiunits) ?? 0,
      })),
    [population, speciesMeta.key],
  );

  // Risk donut: one slice per area, coloured by risk, labels with leader lines
  useEffect(() => {
    const el = riskRef.current;
    if (!el) return;

    if (!rows.length) {
      Plotly.purge(el);
      return;
    }

    const labels = rows.map((r) => r.name);
    const values = rows.map(() => 1);
    const colors = rows.map((r) => RISK_COLORS[r.riskCls]);
    const hover = rows.map(
      (r) => `<b>${r.name}</b><br>Risk: ${r.risk || '—'}`,
    );

    Plotly.newPlot(
      el,
      [
        {
          type: 'pie',
          labels,
          values,
          hole: 0.42,
          marker: {
            colors,
            line: { color: '#ffffff', width: 2 },
          },
          text: labels,
          textinfo: 'text',
          textposition: 'outside',
          automargin: true,
          direction: 'clockwise',
          sort: false,
          hoverinfo: 'text',
          hovertext: hover,
          textfont: { size: 12, color: '#374151' },
          outsidetextfont: { size: 12, color: '#374151' },
          // Leader lines from slices to area names
          showlegend: false,
        },
      ],
      {
        margin: { t: 36, r: 48, b: 36, l: 48 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { size: 12, color: '#374151' },
        annotations: [
          {
            text: `${rows.length}<br>areas`,
            showarrow: false,
            font: { size: 14, color: '#4b5563' },
          },
        ],
      },
      { responsive: true, displayModeBar: false },
    );

    return () => {
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
    };
  }, [rows]);

  // Population treemap
  useEffect(() => {
    const el = treeRef.current;
    if (!el) return;

    const withPop = rows.filter((r) => r.value != null && r.value > 0);
    if (!withPop.length) {
      Plotly.purge(el);
      return;
    }

    Plotly.newPlot(
      el,
      [
        {
          type: 'treemap',
          labels: withPop.map((r) => r.name),
          parents: withPop.map(() => ''),
          values: withPop.map((r) => r.value as number),
          text: withPop.map(
            (r) =>
              `<b>${r.name}</b><br>${(r.value as number).toLocaleString()}`,
          ),
          textinfo: 'text',
          textposition: 'middle center',
          textfont: { size: 15, color: '#1f2937' },
          hoverinfo: 'text',
          hovertext: withPop.map(
            (r) =>
              `<b>${r.name}</b><br>${speciesMeta.label}: ${(r.value as number).toLocaleString()}<br>Risk: ${r.risk || '—'}`,
          ),
          marker: {
            colors: withPop.map((r) => RISK_COLORS[r.riskCls]),
            line: { width: 2, color: '#ffffff' },
            pad: { t: 4, r: 4, b: 4, l: 4 },
          },
          tiling: { pad: 3 },
          pathbar: { visible: false },
        },
      ],
      {
        margin: { t: 8, r: 8, b: 8, l: 8 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        font: { size: 15, color: '#1f2937' },
      },
      { responsive: true, displayModeBar: false },
    );

    return () => {
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
    };
  }, [rows, speciesMeta.label]);

  const missingPopCount = rows.filter((r) => r.value == null || r.value <= 0).length;

  return (
    <div className="space-y-4">
      <h3 className="text-2xl font-black text-gray-800 bg-blue-100 px-4 py-3 rounded">
        {inventoryYear == null
          ? 'Animal Population (no inventory available)'
          : `Animal Population — population from inventory ${inventoryYear}`}
      </h3>

      {inventoryIsFallback && inventoryYear != null && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2">
          No inventory for {year}; showing latest available inventory ({inventoryYear}). Please
          upload {year} data when available.
        </p>
      )}
      {inventoryYear == null && (
        <p className="text-sm text-gray-600">
          No inventory on file for this country. The population chart stays empty until inventory is
          uploaded.
        </p>
      )}

      <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-600 mb-1">
        {RISK_LEGEND.map((item) => (
          <span key={item.cls} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-sm border border-gray-200"
              style={{ backgroundColor: item.color }}
            />
            {item.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-sm font-medium text-gray-700 mb-1">Risk by area</p>
          <p className="text-sm text-gray-600 mb-2 leading-relaxed">
            Each slice is one district or province. Colour shows risk level.
          </p>
          <div ref={riskRef} style={{ width: '100%', height: 360 }} />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <div>
              <p className="text-sm font-medium text-gray-700">Population by area</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Larger cells mean more animals in the inventory for the selected species. Colour
                shows risk level.
              </p>
            </div>
            <label className="text-sm text-gray-700 flex items-center gap-2">
              Species
              <select
                value={species}
                onChange={(e) => setSpecies(e.target.value as CyclePopSpecies)}
                className="px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {SPECIES_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {rows.some((r) => r.value != null && r.value > 0) ? (
            <div ref={treeRef} style={{ width: '100%', height: 360 }} />
          ) : (
            <div className="h-96 flex items-center justify-center text-sm text-gray-500">
              No {speciesMeta.label.toLowerCase()} inventory values for these areas.
            </div>
          )}
          {missingPopCount > 0 && inventoryYear != null && (
            <p className="text-xs text-gray-500 mt-2">
              {missingPopCount} area(s) omitted (no {speciesMeta.label.toLowerCase()} inventory
              value).
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CycleAnimalPopulationViz;
